import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { GlassIconButton } from 'dashboard/components/common';

/**
 * "Build with AI" — describe the business, get a set of real pages.
 *
 * Two things shape this dialog.
 *
 * Generation takes a while (tens of seconds for several pages), which is long
 * enough that a spinner alone reads as a hang. The examples stay on screen
 * while it runs and the button says what is happening, so the wait has
 * something to look at and a reason.
 *
 * And it reports what it did rather than closing on success. Pages whose slug
 * already existed are skipped, never overwritten, and being told "Contact was
 * skipped, it already exists" is the difference between trusting the feature
 * and wondering what it touched.
 */

const EXAMPLES = {
  site: [
    'A family-run Italian restaurant in Prague. Handmade pasta, natural wines, open for lunch and dinner.',
    'A two-person studio selling handmade ceramics online, shipping across Europe.',
    'A mobile bike repair service covering the city, booked by appointment.',
  ],
  // Written as one page rather than one business: for a single page the
  // useful brief says what the page is for, not what the company is.
  page: [
    'An About page: who we are, how the studio started, and the two people who run it.',
    'A Services page listing what we do, who each service suits, and what it costs.',
    'A Contact page with our address, opening hours, and how to reach us.',
  ],
};

export default function AiSiteDialog({ open, onClose, brandId, onCreated, quota }) {
  const [brief, setBrief] = useState('');
  // The whole site, or one page added to the site that is already there.
  const [scope, setScope] = useState('site');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [bundles, setBundles] = useState([]);
  const [buying, setBuying] = useState('');

  useEffect(() => {
    if (open) { setBrief(''); setScope('site'); setBusy(false); setResult(null); setError(''); }
  }, [open]);

  // Only fetched when the allowance has actually run out. Offering to sell
  // credits to someone with four left reads as a shakedown.
  const outOfCredit = quota && quota.remaining_total <= 0;
  useEffect(() => {
    if (!open || !outOfCredit) return;
    API.aiCreditBundles?.({ brand_id: brandId })
      .then((res) => setBundles(Array.isArray(res) ? res : []))
      .catch(() => setBundles([]));
  }, [open, outOfCredit, brandId]);

  const buy = (bundleKey) => {
    setBuying(bundleKey);
    API.aiBuyCredits({
      brand_id: brandId,
      bundle_key: bundleKey,
      return_base: window.location.origin,
    })
      .then((res) => {
        if (res?.url) window.location.assign(res.url);
        else toast.error('Could not start checkout');
      })
      .catch((err) => toast.error(err?.message || 'Could not start checkout'))
      .finally(() => setBuying(''));
  };

  const generate = () => {
    setBusy(true);
    setError('');
    setResult(null);
    API.aiGenerateSite({ brand_id: brandId, brief, scope })
      .then((res) => {
        setResult(res);
        const n = res?.created?.length || 0;
        if (n > 0) {
          toast.success(`${n} page${n === 1 ? '' : 's'} created`);
          onCreated?.();
        } else {
          setError('Nothing new was created — every page it suggested already exists.');
        }
      })
      .catch((err) => setError(err?.message || 'Generation failed'))
      .finally(() => setBusy(false));
  };

  const tooShort = brief.trim().length < 10;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth='sm' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        <Stack direction='row' alignItems='center' spacing='8px'>
          <AutoAwesomeIcon fontSize='small' color='primary' />
          <span>Build with AI</span>
        </Stack>
        {!busy && (
          <GlassIconButton
            label='Close'
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size='small'
          >
            <CloseIcon fontSize='small' />
          </GlassIconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {result ? (
          <Box>
            <Stack direction='row' alignItems='center' spacing='8px' mb='10px'>
              <CheckCircleIcon color='success' />
              <Typography fontWeight='700'>
                {result.created.length} page{result.created.length === 1 ? '' : 's'} created
              </Typography>
            </Stack>
            <Stack spacing='6px' mb={result.skipped?.length ? '14px' : 0}>
              {result.created.map((p) => (
                <Stack key={p._id} direction='row' alignItems='center' spacing='8px'>
                  <Chip size='small' label={`/${p.slug}`} />
                  <Typography variant='body2'>{p.title}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {p.sections} section{p.sections === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {result.skipped?.length > 0 && (
              <Alert severity='info' sx={{ borderRadius: '10px' }}>
                Skipped {result.skipped.map((s) => `/${s}`).join(', ')} — those already exist.
                Nothing you had was changed.
              </Alert>
            )}
            <Typography variant='body2' color='text.secondary' mt='14px'>
              Everything is a draft. Open a page to edit the copy and publish when you are happy
              with it.
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* First, because it changes what the box below should say: for a
                site you describe the business, for one page you describe the
                page. Asking for the brief first and the scope second would
                have people writing the wrong one. */}
            <ToggleButtonGroup
              exclusive
              size='small'
              value={scope}
              disabled={busy}
              onChange={(_e, v) => { if (v) { setScope(v); setBrief(''); } }}
              sx={{ mb: '14px' }}
            >
              <ToggleButton value='site'>Whole website</ToggleButton>
              <ToggleButton value='page'>One page</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant='body2' color='text.secondary' mb='12px'>
              {scope === 'site'
                ? 'Describe the business in a sentence or two — what it sells, who it is for, what '
                  + 'makes it different. The more specific you are, the better the copy.'
                : 'Describe the page — what it is for and what should be on it. It is written to '
                  + 'sit inside the site you already have, so you do not need to explain the '
                  + 'business again.'}
            </Typography>
            <TextField
              autoFocus
              multiline
              minRows={4}
              fullWidth
              value={brief}
              disabled={busy}
              onChange={(e) => setBrief(e.target.value)}
              placeholder={scope === 'site'
                ? 'A family-run bakery in Brno, sourdough and pastries, open from 7am…'
                : 'An About page covering how the bakery started and who bakes each morning…'}
            />

            <Typography variant='caption' color='text.secondary' display='block' mt='14px' mb='6px'>
              Or start from an example
            </Typography>
            <Stack spacing='6px'>
              {EXAMPLES[scope].map((ex) => (
                <Box
                  key={ex}
                  role='button'
                  onClick={() => !busy && setBrief(ex)}
                  sx={{
                    p: '8px 10px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: busy ? 'default' : 'pointer',
                    opacity: busy ? 0.5 : 1,
                    fontSize: 13,
                    '&:hover': { borderColor: busy ? 'divider' : 'primary.main' },
                  }}
                >
                  {ex}
                </Box>
              ))}
            </Stack>

            {error && (
              <Alert severity='warning' sx={{ borderRadius: '10px', mt: '14px' }}>
                {error}
              </Alert>
            )}

            <Typography variant='caption' color='text.secondary' display='block' mt='14px'>
              {scope === 'site'
                ? 'Pages are created as drafts and nothing existing is changed. A page whose '
                  + 'address is already in use is skipped.'
                : 'The page is created as a draft and nothing existing is changed. It will not '
                  + 'become your homepage. If its address is already in use it is skipped.'}
            </Typography>

            {/* Shown before generating, not after a refusal. Finding out you
                have run out at the moment you press the button is the part
                that annoys people. */}
            {outOfCredit && bundles.length > 0 && (
              <Box sx={{ mt: '14px', p: '12px', borderRadius: '10px', bgcolor: 'action.hover' }}>
                <Typography variant='body2' fontWeight='700' mb='2px'>
                  Out of generations this month
                </Typography>
                <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
                  Credits never expire and are only used once your monthly allowance is gone.
                </Typography>
                <Stack direction='row' spacing='8px' flexWrap='wrap' useFlexGap>
                  {bundles.map((b) => (
                    <Button
                      key={b.key}
                      size='small'
                      variant='outlined'
                      disabled={Boolean(buying)}
                      onClick={() => buy(b.key)}
                    >
                      {buying === b.key ? 'Opening…' : `${b.label} — €${b.amount}`}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}

            {quota && (
              <Typography variant='caption' color='text.secondary' display='block' mt='6px'>
                {quota.remaining_included > 0
                  ? `${quota.remaining_included} of ${quota.included} included generations left this month.`
                  : quota.credits > 0
                    ? `Monthly allowance used — this will use 1 of your ${quota.credits} credits.`
                    : 'You have used this month’s allowance. Add credits to keep going.'}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: '14px 20px' }}>
        {result ? (
          <Button variant='contained' onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              variant='contained'
              onClick={generate}
              disabled={tooShort || busy}
              startIcon={busy
                ? <CircularProgress size={16} thickness={5} color='inherit' />
                : <AutoAwesomeIcon />}
            >
              {busy
                ? (scope === 'site' ? 'Writing your site…' : 'Writing your page…')
                : 'Generate'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
